// src/components/AdmissionFormTD.tsx
import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import {
  CountryIso2,
  defaultCountries,
  parseCountry,
} from "react-international-phone";
import { selectStyles } from "../../assets/selectStyles";
import axios from "axios";
import { dynamicFailed, dynamicSuccess } from "../../hooks/custom/useToast";
import { useLoader } from "../../context/LoaderContext";
import Offcanvas from "bootstrap/js/dist/offcanvas";
import { useNavigate } from "react-router-dom";
import {
  base64ToFile,
  fileToBase64,
  getFormattedDate,
  sendNotification,
} from "../../exports/functions";
import PoliciesDrawer from "../../pages/PoliciesDrawer";
import {
  IAdmissionPolicyData,
  IScholarshipsAndFinancialData,
} from "../../exports/interfaces";
import { PDFDocument } from "pdf-lib";
import DownloadApplicationForm from "./DownloadApplicationForm";
import { pdf } from "@react-pdf/renderer";
import bankQr from "../../assets/img/bankQr.jpeg";
import { useMobile } from "../../hooks/custom/useMobile";
import useTab from "../../hooks/custom/useTab";
import { cmsEnv } from "../../exports/exports";
import { useCompanyDetailsContext } from "../../context/CompanyDetailsContext";

type DocumentFileKey =
  | "citizenshipFile"
  | "cvFile"
  | "seeCertificateFile"
  | "plus2certificateFile"
  | "profileFile";

type AwardsFileKey = "academicAwardFile" | "otherDegreeFile";

interface Errors {
  [key: string]: string;
}

const RefactoredApplicationForm = ({
  admissionPolicy,
  scholarshipsAndFinancial,
}: {
  admissionPolicy: IAdmissionPolicyData[];
  scholarshipsAndFinancial: IScholarshipsAndFinancialData[];
}) => {
  const navigate = useNavigate();
  const { companyDetailsData } = useCompanyDetailsContext();

  const mobile = useMobile();
  const tab = useTab();

  const { setLoading } = useLoader();

  const handleButtonClick = async (
    action: () => void | Promise<void>,
    message: string,
  ) => {
    const confirmed = window.confirm(message);

    if (confirmed) {
      await action();
    }
  };

  const handleDownloadPDF = async (formData: any) => {
    const generatedBlob = await pdf(
      <DownloadApplicationForm formData={formData} />,
    ).toBlob();

    const mergedPdf = await PDFDocument.create();

    const generatedBytes = await generatedBlob.arrayBuffer();

    const generatedPdf = await PDFDocument.load(generatedBytes);

    const generatedPages = await mergedPdf.copyPages(
      generatedPdf,
      generatedPdf.getPageIndices(),
    );

    generatedPages.forEach((page) => {
      mergedPdf.addPage(page);
    });

    const uploadedFiles = [
      formData.academicAwardFile,
      formData.otherDegreeFile,
      formData.paymentFile,
      formData.citizenshipFile,
      formData.cvFile,
      formData.sopFile,
      formData.seeCertificateFile,
      formData.plus2certificateFile,
      formData.profileFile,
    ];

    for (const file of uploadedFiles) {
      if (file && file.type === "application/pdf") {
        try {
          const bytes = await file.arrayBuffer();

          const pdfDoc = await PDFDocument.load(bytes);

          const copiedPages = await mergedPdf.copyPages(
            pdfDoc,
            pdfDoc.getPageIndices(),
          );

          copiedPages.forEach((page) => {
            mergedPdf.addPage(page);
          });
        } catch (error) {
          console.error("Failed to merge PDF:", file.name, error);
        }
      }
    }

    const finalPdfBytes = await mergedPdf.save();

    const finalBlob = new Blob([finalPdfBytes.buffer as ArrayBuffer], {
      type: "application/pdf",
    });

    const url = URL.createObjectURL(finalBlob);

    const link = document.createElement("a");

    link.href = url;

    link.download = formData.firstName
      ? `${formData.firstName}_UNepal_application_form.pdf`
      : "UNepal_application_form.pdf";

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const [sameAsCurrent, setSameAsCurrent] = useState(false);

  const [academicAwardYesNo, setAcademicAwardYesNo] = useState("");
  const [otherDegreeYesNo, setOtherDegreeYesNo] = useState("");
  const [internYesNo, setInternYesNo] = useState("");

  const [selectedCountry, setSelectedCountry] = useState<CountryIso2>("np");
  const [refree1Country, setRefree1Country] = useState<CountryIso2>("np");
  const [refree2Country, setRefree2Country] = useState<CountryIso2>("np");

  const citizenshipFileRef = useRef<HTMLInputElement>(null);
  const cvFileRef = useRef<HTMLInputElement>(null);
  const sopFileRef = useRef<HTMLInputElement>(null);
  const seeCertificateFileRef = useRef<HTMLInputElement>(null);
  const plus2certificateFileRef = useRef<HTMLInputElement>(null);
  const profileFileRef = useRef<HTMLInputElement>(null);
  const paymentFileRef = useRef<HTMLInputElement>(null);
  const academicAwardFileRef = useRef<HTMLInputElement>(null);
  const otherDegreeFileRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    studies: "",
    firstPriorityProgram: "",
    secondPriorityProgram: "",
    thirdPriorityProgram: "",
    firstName: "",
    middleName: "",
    lastName: "",
    dob: "",
    gender: "",
    nationality: "",
    ethnicity: "",
    email: "",
    phone: "",
    currentAddress: "",
    permanentAddress: "",
    guardianName: "",
    guardianRelation: "",
    plus2institution: "",
    yearOfGraduation: "",
    academicAwardFile: null as File | null,
    otherDegreeFile: null as File | null,
    paymentFile: null as File | null,
    internOrg: "",
    internDuration: "",
    financePlan: "",
    referee1Name: "",
    referee1Email: "",
    referee1Mobile: "",
    referee2Name: "",
    referee2Email: "",
    referee2Mobile: "",
    citizenshipFile: null as File | null,
    cvFile: null as File | null,
    sopFile: null as File | null,
    seeCertificateFile: null as File | null,
    plus2certificateFile: null as File | null,
    profileFile: null as File | null,
    declaration: false,
    scholarship: "",
    scholarshipTypes: [] as string[],
  });

  const [errors, setErrors] = useState<Errors>({});
  const formRef = useRef<HTMLDivElement>(null);

  const countryOptions = defaultCountries.map((c) => {
    const country = parseCountry(c);
    return {
      value: country.iso2,
      label: country.name,
      dialCode: country.dialCode,
      iso2: country.iso2,
    };
  });

  const resetForm = () => {
    localStorage.removeItem("applicationFormData");

    setFormData({
      studies: "",
      firstPriorityProgram: "",
      secondPriorityProgram: "",
      thirdPriorityProgram: "",
      firstName: "",
      middleName: "",
      lastName: "",
      dob: "",
      gender: "",
      nationality: "",
      ethnicity: "",
      email: "",
      phone: "",
      currentAddress: "",
      permanentAddress: "",
      guardianName: "",
      guardianRelation: "",
      plus2institution: "",
      yearOfGraduation: "",
      academicAwardFile: null,
      otherDegreeFile: null,
      paymentFile: null,
      internOrg: "",
      internDuration: "",
      financePlan: "",
      referee1Name: "",
      referee1Email: "",
      referee1Mobile: "",
      referee2Name: "",
      referee2Email: "",
      referee2Mobile: "",
      citizenshipFile: null,
      cvFile: null,
      sopFile: null,
      seeCertificateFile: null,
      plus2certificateFile: null,
      profileFile: null,
      declaration: false,
      scholarship: "",
      scholarshipTypes: [],
    });
    setAcademicAwardYesNo("");
    setInternYesNo("");
    setOtherDegreeYesNo("");
    setSameAsCurrent(false);
    setSelectedCountry("np");
    setRefree1Country("np");
    setRefree2Country("np");
    setErrors({});
    if (citizenshipFileRef.current) citizenshipFileRef.current.value = "";
    if (cvFileRef.current) cvFileRef.current.value = "";
    if (plus2certificateFileRef.current)
      plus2certificateFileRef.current.value = "";
    if (seeCertificateFileRef.current) seeCertificateFileRef.current.value = "";
    if (profileFileRef.current) profileFileRef.current.value = "";
    if (paymentFileRef.current) paymentFileRef.current.value = "";
  };

  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

  const isValidFile = (file: File | null) => {
    if (!file) return false;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];
    return allowed.includes(file.type);
  };

  const validateForm = (): boolean => {
    const newErrors: Errors = {};
    let firstErrorField: string | null = null;

    // Required text fields
    if (!formData.studies) {
      newErrors.studies = "Studies is required";
      if (!firstErrorField) firstErrorField = "studies";
    }
    if (!formData.firstPriorityProgram) {
      newErrors.firstPriorityProgram = "First priority program is required";
      if (!firstErrorField) firstErrorField = "firstPriorityProgram";
    }
    if (!formData.secondPriorityProgram) {
      newErrors.secondPriorityProgram = "Second priority program is required";
      if (!firstErrorField) firstErrorField = "secondPriorityProgram";
    }
    if (!formData.thirdPriorityProgram) {
      newErrors.thirdPriorityProgram = "Third priority program is required";
      if (!firstErrorField) firstErrorField = "thirdPriorityProgram";
    }
    if (!formData.firstName) {
      newErrors.firstName = "First name is required";
      if (!firstErrorField) firstErrorField = "firstName";
    }
    if (!formData.lastName) {
      newErrors.lastName = "Last name is required";
      if (!firstErrorField) firstErrorField = "lastName";
    }
    if (!formData.dob) {
      newErrors.dob = "Date of birth is required";
      if (!firstErrorField) firstErrorField = "dob";
    }
    if (!formData.email) {
      newErrors.email = "Email is required";
      if (!firstErrorField) firstErrorField = "email";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Invalid email format";
      if (!firstErrorField) firstErrorField = "email";
    }
    if (!formData.phone) {
      newErrors.phone = "Phone is required";
      if (!firstErrorField) firstErrorField = "phone";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Phone must be exactly 10 digits";
      if (!firstErrorField) firstErrorField = "phone";
    }
    if (!formData.currentAddress) {
      newErrors.currentAddress = "Current address is required";
      if (!firstErrorField) firstErrorField = "currentAddress";
    }
    if (!formData.permanentAddress) {
      newErrors.permanentAddress = "Permanent address is required";
      if (!firstErrorField) firstErrorField = "permanentAddress";
    }
    if (!formData.guardianName) {
      newErrors.guardianName = "Guardian name is required";
      if (!firstErrorField) firstErrorField = "guardianName";
    }
    if (!formData.guardianRelation) {
      newErrors.guardianRelation = "Guardian relation is required";
      if (!firstErrorField) firstErrorField = "guardianRelation";
    }
    if (formData.referee1Email && !isValidEmail(formData.referee1Email)) {
      newErrors.referee1Email = "Invalid email format";
      if (!firstErrorField) firstErrorField = "referee1Email";
    }
    if (formData.referee1Mobile && !/^\d{10}$/.test(formData.referee1Mobile)) {
      newErrors.referee1Mobile = "Phone must be exactly 10 digits";
      if (!firstErrorField) firstErrorField = "referee1Mobile";
    }
    if (formData.referee2Mobile && !/^\d{10}$/.test(formData.referee2Mobile)) {
      newErrors.referee2Mobile = "Phone must be exactly 10 digits";
      if (!firstErrorField) firstErrorField = "referee2Mobile";
    }

    // Conditional Yes/No + File
    const checkYesNoFile = (
      yesNo: string,
      file: File | null,
      yesNoKey: string,
      fileKey: string,
    ) => {
      if (!yesNo) {
        newErrors[yesNoKey] = "This field is required";
        if (!firstErrorField) firstErrorField = yesNoKey;
      } else if (yesNo === "yes") {
        if (!file) {
          newErrors[fileKey] = "File upload is required";
          if (!firstErrorField) firstErrorField = fileKey;
        } else if (!isValidFile(file)) {
          newErrors[fileKey] = "Only PNG, JPG, JPEG, PDF allowed";
          if (!firstErrorField) firstErrorField = fileKey;
        }
      }
    };

    checkYesNoFile(
      academicAwardYesNo,
      formData.academicAwardFile,
      "academicAward",
      "academicAwardFile",
    );

    // Internship
    if (!internYesNo) {
      newErrors.internYesNo = "Internship selection is required";
      if (!firstErrorField) firstErrorField = "internYesNo";
    } else if (internYesNo === "yes") {
      if (!formData.internOrg) {
        newErrors.internOrg = "Organization name is required";
        if (!firstErrorField) firstErrorField = "internOrg";
      }
      if (!formData.internDuration) {
        newErrors.internDuration = "Duration is required";
        if (!firstErrorField) firstErrorField = "internDuration";
      }
    }

    // Finance Plan
    if (!formData.financePlan) {
      newErrors.financePlan = "Please select a finance plan";
      if (!firstErrorField) firstErrorField = "financePlan";
    }

    // Scholarship Plan
    if (!formData.scholarship) {
      newErrors.scholarship = "Please select an option";
      if (!firstErrorField) firstErrorField = "scholarship";
    }

    // if (!formData.referee1Name) {
    //   newErrors.referee1Name = "Full name is required";
    //   if (!firstErrorField) firstErrorField = "referee1Name";
    // }
    // if (!formData.referee1Email) {
    //   newErrors.referee1Email = "Email is required";
    //   if (!firstErrorField) firstErrorField = "referee1Email";
    // } else if (!isValidEmail(formData.referee1Email)) {
    //   newErrors.referee1Email = "Invalid email format";
    //   if (!firstErrorField) firstErrorField = "referee1Email";
    // }
    // if (!formData.referee1Mobile) {
    //   newErrors.referee1Mobile = "Mobile number is required";
    //   if (!firstErrorField) firstErrorField = "referee1Mobile";
    // } else if (!/^\d{10}$/.test(formData.referee1Mobile)) {
    //   newErrors.referee1Mobile = "Mobile must be exactly 10 digits";
    //   if (!firstErrorField) firstErrorField = "referee1Mobile";
    // }

    // if (!formData.referee2Name) {
    //   newErrors.referee2Name = "Full name is required";
    //   if (!firstErrorField) firstErrorField = "referee2Name";
    // }
    // if (!formData.referee2Email) {
    //   newErrors.referee2Email = "Email is required";
    //   if (!firstErrorField) firstErrorField = "referee2Email";
    // } else if (!isValidEmail(formData.referee1Email)) {
    //   newErrors.referee2Email = "Invalid email format";
    //   if (!firstErrorField) firstErrorField = "referee2Email";
    // }
    // if (!formData.referee1Mobile) {
    //   newErrors.referee2Mobile = "Mobile number is required";
    //   if (!firstErrorField) firstErrorField = "referee2Mobile";
    // } else if (!/^\d{10}$/.test(formData.referee2Mobile)) {
    //   newErrors.referee2Mobile = "Mobile must be exactly 10 digits";
    //   if (!firstErrorField) firstErrorField = "refere21Mobile";
    // }

    // Supporting Document
    const checkSupportingDocument = (file: File | null, fileKey: string) => {
      if (!file) {
        newErrors[fileKey] = "File upload is required";
        if (!firstErrorField) firstErrorField = fileKey;
      } else if (!isValidFile(file)) {
        newErrors[fileKey] = "Only PNG, JPG, JPEG, PDF allowed";
        if (!firstErrorField) firstErrorField = fileKey;
      }
    };

    // checkSupportingDocument(formData.citizenshipFile, "citizenshipFile");
    // checkSupportingDocument(formData.cvFile, "cvFile");
    // checkSupportingDocument(formData.sopFile, "sopFile");
    // checkSupportingDocument(formData.seeCertificateFile, "seeCertificateFile");
    // checkSupportingDocument(
    //   formData.plus2certificateFile,
    //   "plus2certificateFile",
    // );
    // checkSupportingDocument(formData.profileFile, "profileFile");
    checkSupportingDocument(formData.paymentFile, "paymentFile");

    // Declaration
    if (!formData.declaration) {
      newErrors.declaration = "You must agree to the declaration";
      if (!firstErrorField) firstErrorField = "declaration";
    }

    setErrors(newErrors);

    // Scroll to first error
    if (firstErrorField) {
      const el = document.getElementById(firstErrorField);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const undergraduatePrograms = [
    {
      category:
        "Computer/Data Science, Mathematics, and Artificial Intelligence (AI)",
      programs: [
        "Bachelor of Science in Computer Science and Information Technology (CSIT)",
        "Bachelor of Science in Data Science and Artificial Intelligence (DSAI)",
      ],
    },
    {
      category: "Physics, Chemistry, Biology, and Earth Sciences (PCBES)",
      programs: ["Bachelor of Science in Integrated Science"],
    },
    {
      category: "Cognitive Science: Mind, Brain, and Behavior (MBB)",
      programs: ["Bachelor of Arts in Psychology"],
    },
    {
      category: "Economics, Finance, and Business Studies (EFB)",
      programs: [
        "Bachelor of Arts in Finance and Economics",
        "Bachelor of Arts in Economics",
        "Bachelor of Science in Business Analytics",
      ],
    },
    {
      category:
        "Energy, Environment, Climate Change, and Sustainability (EECCS)",
      programs: [
        "Bachelor of Science in Sustainable Energy Technology and Systems (SETS)",
        "Bachelor of Science in Environmental Technology and Management (ETM)",
        "Bachelor of Science in Climate Change and Sustainability (CCS)",
      ],
    },
    {
      category: "Philosophy, Politics, Economics, and Law (PPEL)",
      programs: [
        "Bachelor of Arts in Philosophy, Politics, Economics and Law (PPEL)",
      ],
    },
    {
      category: "Literature, Communication and Media Studies (LCMS)",
      programs: [
        "Bachelor of Arts in English Studies",
        "Bachelor of Arts in Communication and Media Studies (CMS)",
      ],
    },
    {
      category: "History, Culture, and Society (HCS)",
      programs: [
        "Bachelor of Arts in Social Sciences",
        "Bachelor of Arts in Gender and Development Studies (GDS)",
      ],
    },
  ];

  useEffect(() => {
    const loadFormData = async () => {
      const savedData = localStorage.getItem("applicationFormData");

      if (!savedData) return;

      const parsedData = JSON.parse(savedData);

      const savedFormData = parsedData.formData;

      setFormData({
        ...savedFormData,

        academicAwardFile: savedFormData.academicAwardFile
          ? await base64ToFile(
              savedFormData.academicAwardFile.base64,
              savedFormData.academicAwardFile.name,
              savedFormData.academicAwardFile.type,
            )
          : null,

        otherDegreeFile: savedFormData.otherDegreeFile
          ? await base64ToFile(
              savedFormData.otherDegreeFile.base64,
              savedFormData.otherDegreeFile.name,
              savedFormData.otherDegreeFile.type,
            )
          : null,

        paymentFile: savedFormData.paymentFile
          ? await base64ToFile(
              savedFormData.paymentFile.base64,
              savedFormData.paymentFile.name,
              savedFormData.paymentFile.type,
            )
          : null,

        citizenshipFile: savedFormData.citizenshipFile
          ? await base64ToFile(
              savedFormData.citizenshipFile.base64,
              savedFormData.citizenshipFile.name,
              savedFormData.citizenshipFile.type,
            )
          : null,

        cvFile: savedFormData.cvFile
          ? await base64ToFile(
              savedFormData.cvFile.base64,
              savedFormData.cvFile.name,
              savedFormData.cvFile.type,
            )
          : null,

        sopFile: savedFormData.sopFile
          ? await base64ToFile(
              savedFormData.sopFile.base64,
              savedFormData.sopFile.name,
              savedFormData.sopFile.type,
            )
          : null,

        seeCertificateFile: savedFormData.seeCertificateFile
          ? await base64ToFile(
              savedFormData.seeCertificateFile.base64,
              savedFormData.seeCertificateFile.name,
              savedFormData.seeCertificateFile.type,
            )
          : null,

        plus2certificateFile: savedFormData.plus2certificateFile
          ? await base64ToFile(
              savedFormData.plus2certificateFile.base64,
              savedFormData.plus2certificateFile.name,
              savedFormData.plus2certificateFile.type,
            )
          : null,

        profileFile: savedFormData.profileFile
          ? await base64ToFile(
              savedFormData.profileFile.base64,
              savedFormData.profileFile.name,
              savedFormData.profileFile.type,
            )
          : null,
      });

      // Restore separate states
      setSameAsCurrent(parsedData.sameAsCurrent ?? false);

      setAcademicAwardYesNo(parsedData.academicAwardYesNo ?? "");

      setOtherDegreeYesNo(parsedData.otherDegreeYesNo ?? "");

      setInternYesNo(parsedData.internYesNo ?? "");

      setSelectedCountry(parsedData.selectedCountry ?? "np");

      setRefree1Country(parsedData.refree1Country ?? "np");

      setRefree2Country(parsedData.refree2Country ?? "np");
    };

    loadFormData();
  }, []);

  const saveToLocalStorage = async () => {
    const dataToSave = {
      formData: {
        ...formData,

        academicAwardFile: formData.academicAwardFile
          ? await fileToBase64(formData.academicAwardFile)
          : null,

        otherDegreeFile: formData.otherDegreeFile
          ? await fileToBase64(formData.otherDegreeFile)
          : null,

        paymentFile: formData.paymentFile
          ? await fileToBase64(formData.paymentFile)
          : null,

        citizenshipFile: formData.citizenshipFile
          ? await fileToBase64(formData.citizenshipFile)
          : null,

        cvFile: formData.cvFile ? await fileToBase64(formData.cvFile) : null,

        sopFile: formData.sopFile ? await fileToBase64(formData.sopFile) : null,

        seeCertificateFile: formData.seeCertificateFile
          ? await fileToBase64(formData.seeCertificateFile)
          : null,

        plus2certificateFile: formData.plus2certificateFile
          ? await fileToBase64(formData.plus2certificateFile)
          : null,

        profileFile: formData.profileFile
          ? await fileToBase64(formData.profileFile)
          : null,
      },

      sameAsCurrent,
      academicAwardYesNo,
      otherDegreeYesNo,
      internYesNo,
      selectedCountry,
      refree1Country,
      refree2Country,
    };

    try {
      localStorage.setItem("applicationFormData", JSON.stringify(dataToSave));
    } finally {
      dynamicSuccess("Application Form has been saved.");
    }
  };

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    setLoading(true);

    // Create FormData object
    const formDataToSend = new FormData();

    // Append all text/string fields
    formDataToSend.append("studies", formData.studies);
    formDataToSend.append(
      "first_priority_program",
      formData.firstPriorityProgram,
    );
    formDataToSend.append(
      "second_priority_program",
      formData.secondPriorityProgram,
    );
    formDataToSend.append(
      "third_priority_program",
      formData.thirdPriorityProgram,
    );
    formDataToSend.append("first_name", formData.firstName.trim());
    formDataToSend.append("middle_name", formData.middleName.trim());
    formDataToSend.append("last_name", formData.lastName.trim());
    formDataToSend.append("date_of_birth", formData.dob);
    formDataToSend.append("gender", formData.gender);
    formDataToSend.append("nationality", formData.nationality);
    formDataToSend.append("ethnicity", formData.ethnicity);
    formDataToSend.append("email", formData.email.trim());
    formDataToSend.append("phone", formData.phone);
    formDataToSend.append("current_address", formData.currentAddress.trim());
    formDataToSend.append(
      "permanent_address",
      formData.permanentAddress.trim(),
    );
    formDataToSend.append("guardian_full_name", formData.guardianName.trim());
    formDataToSend.append(
      "guardian_relation",
      formData.guardianRelation.trim(),
    );
    formDataToSend.append("10_2_institution", formData.plus2institution);
    formDataToSend.append("year_of_graduation", formData.yearOfGraduation);
    formDataToSend.append("referee_name_1", formData.referee1Name);
    formDataToSend.append("referee_email_1", formData.referee1Email);
    formDataToSend.append("referee_contact_1", formData.referee1Mobile);
    formDataToSend.append("referee_name_2", formData.referee2Name);
    formDataToSend.append("referee_email_2", formData.referee2Email);
    formDataToSend.append("referee_contact_2", formData.referee2Mobile);
    formDataToSend.append("education_finance", formData.financePlan);
    formDataToSend.append(
      "consideration_for_scholarship",
      formData.scholarshipTypes.join(", "),
    );

    // Work & Internship details (only if "yes")
    if (internYesNo === "yes") {
      formDataToSend.append(
        "internship_organization",
        formData.internOrg.trim(),
      );
      formDataToSend.append(
        "internship_duration_months",
        formData.internDuration,
      );
    }

    // Append files (only if selected)

    if (formData.otherDegreeFile) {
      formDataToSend.append("other_degrees", formData.otherDegreeFile);
    }

    if (formData.academicAwardFile) {
      formDataToSend.append(
        "awards_and_certificates",
        formData.academicAwardFile,
      );
    }

    if (formData.citizenshipFile) {
      formDataToSend.append("government_id", formData.citizenshipFile);
    }
    if (formData.cvFile) {
      formDataToSend.append("cv", formData.cvFile);
    }
    if (formData.sopFile) {
      formDataToSend.append("sop", formData.sopFile);
    }
    if (formData.seeCertificateFile) {
      formDataToSend.append("see_certificate", formData.seeCertificateFile);
    }
    if (formData.plus2certificateFile) {
      formDataToSend.append("10_2_certificate", formData.plus2certificateFile);
    }
    if (formData.profileFile) {
      formDataToSend.append("profile_photo", formData.profileFile);
    }
    if (formData.paymentFile) {
      formDataToSend.append("payment_proof", formData.paymentFile);
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_cmsUrl}/content-manager/${process.env.REACT_APP_cmsProjectId}/application-form`,
        formDataToSend,
        {
          headers: {
            ...cmsEnv,
            "Content-Type": "multipart/form-data",
            "x-api-key": process.env.REACT_APP_cmsToken,
          },
        },
      );

      if (response.status === 200 || response.status === 201) {
        resetForm();
        // dynamicSuccess("Application submitted successfully!");

        const recieverNotificationData = {
          templateId: "43d6008d-5d87-40d5-bd64-f91b2a44d376",
          senderId: 5636,
          payload: {
            fullName:
              formData.firstName +
              " " +
              formData.middleName +
              " " +
              formData.lastName,
            email: formData.email,
            phone: formData.phone,
            date: getFormattedDate(),
            applicationType: "Undergraduate",
          },
          users: [
            {
              sisID: Math.floor(Math.random() * 9000) + 1000,
              email: "admission@unepal.edu.np",
            },
          ],
        };
        const senderNotificationData = {
          templateId: "710bd302-6851-4d15-9db4-d32f6db64237",
          senderId: 5636,
          payload: {
            fullName:
              formData.firstName +
              " " +
              formData.middleName +
              " " +
              formData.lastName,
            applicationType: "the undergraduate program",
          },
          users: [
            {
              sisID: Math.floor(Math.random() * 9000) + 1000,
              email: formData.email,
            },
          ],
        };
        sendNotification(recieverNotificationData);
        sendNotification(senderNotificationData);
        navigate("/application-success#admission");
        setLoading(false);
      } else {
        dynamicFailed("Submission failed!");
        setLoading(false);
      }
    } catch (error: any) {
      dynamicFailed("Submission failed!");
      setLoading(false);
    }
  };

  const openOffcanvas = (type: "admission" | "scholarship") => {
    const event = new CustomEvent("SET_POLICY_TAB", { detail: type });
    window.dispatchEvent(event);

    const el = document.getElementById("admissionOffcanvas");
    if (!el) return;

    let instance = Offcanvas.getInstance(el);
    if (!instance) {
      instance = new Offcanvas(el);
    }
    instance.show();
  };

  useEffect(() => {
    window.location.hash.replace("#", "") === "financial-aid"
      ? openOffcanvas("scholarship")
      : openOffcanvas("admission");
  }, []);

  return (
    <>
      <section ref={formRef}>
        <div className="td_height_80 td_height_lg_40" />
        <div className="container">
          <div className="row">
            <div className="col-lg-10 offset-lg-1" style={{ padding: 0 }}>
              <div className="td_form_card td_style_1 td_radius_10 td_gray_bg_5 custom_td_form_card">
                <div className="td_form_card_in">
                  <h2 className="td_fs_36 td_mb_20 td_mb_16 td_center">
                    Application Form
                  </h2>

                  <div className="row">
                    <div className="col-lg-12">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        Inquiry Now / Contact Now
                      </h3>
                      <div className="td_mb_40">
                        <p>
                          <i className="fa-solid fa-phone me-2"></i>
                          <a
                            href={`tel:${companyDetailsData[0]?.telephone_number}`}
                          >
                            {companyDetailsData[0]?.telephone_number}
                          </a>
                        </p>
                        <p>
                          <i className="fa-solid fa-mobile me-2"></i>
                          <a
                            href={`tel:${companyDetailsData[0]?.mobile_number}`}
                          >
                            {companyDetailsData[0]?.mobile_number}
                          </a>
                        </p>
                        <p>
                          <i className="fa-solid fa-envelope me-2"></i>
                          <a href={`mailto:${companyDetailsData[0]?.email}`}>
                            {companyDetailsData[0]?.email}
                          </a>
                        </p>
                        <p>
                          <i className="fa-solid fa-globe me-2"></i>
                          <a
                            href={companyDetailsData[0]?.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {companyDetailsData[0]?.url}
                          </a>
                        </p>
                        <p>
                          <i className="fa-solid fa-location-dot me-2"></i>
                          <a
                            href={companyDetailsData[0]?.get_direction}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {companyDetailsData[0]?.company_address}
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-12">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        Program *
                      </h3>
                      <div className="td_mb_40">
                        <select
                          id="studies"
                          className={`td_form_field td_mb_8 td_medium td_white_bg`}
                          value={formData.studies}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              studies: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Program</option>
                          <option value="Undergraduate">Undergraduate</option>
                          <option disabled value="Graduate">
                            Graduate
                          </option>
                        </select>

                        {errors.studies && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.studies}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-12">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        First Priority Degree *
                      </h3>
                      <div className="td_mb_40">
                        <select
                          id="firstPriorityProgram"
                          className={`td_form_field td_mb_8 td_medium td_white_bg`}
                          value={formData.firstPriorityProgram}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              firstPriorityProgram: e.target.value,
                              secondPriorityProgram:
                                formData.secondPriorityProgram ===
                                e.target.value
                                  ? ""
                                  : formData.secondPriorityProgram,
                              thirdPriorityProgram:
                                formData.thirdPriorityProgram === e.target.value
                                  ? ""
                                  : formData.thirdPriorityProgram,
                            })
                          }
                        >
                          <option value="">Select Degree</option>

                          {formData.studies === "Undergraduate" ? (
                            undergraduatePrograms.map((group) => (
                              <optgroup
                                key={group.category}
                                label={group.category}
                              >
                                {group.programs.map((program) => (
                                  <option key={program} value={program}>
                                    {program}
                                  </option>
                                ))}
                              </optgroup>
                            ))
                          ) : formData.studies === "Graduate" ? (
                            <option value="Master of Public Policy (MPP)">
                              Master of Public Policy (MPP)
                            </option>
                          ) : null}
                        </select>
                        {errors.firstPriorityProgram && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.firstPriorityProgram}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-12">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        Second Priority Degree *
                      </h3>
                      <div className="td_mb_40">
                        <select
                          id="secondPriorityProgram"
                          className={`td_form_field td_mb_8 td_medium td_white_bg`}
                          value={formData.secondPriorityProgram}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              secondPriorityProgram: e.target.value,
                              thirdPriorityProgram:
                                formData.thirdPriorityProgram === e.target.value
                                  ? ""
                                  : formData.thirdPriorityProgram,
                            })
                          }
                        >
                          <option value="">Select Degree</option>

                          {formData.studies === "Undergraduate" ? (
                            undergraduatePrograms.map((group) => {
                              const filteredPrograms = group.programs.filter(
                                (program) =>
                                  program !== formData.firstPriorityProgram,
                              );

                              if (filteredPrograms.length === 0) return null;

                              return (
                                <optgroup
                                  key={group.category}
                                  label={group.category}
                                >
                                  {filteredPrograms.map((program) => (
                                    <option key={program} value={program}>
                                      {program}
                                    </option>
                                  ))}
                                </optgroup>
                              );
                            })
                          ) : formData.studies === "Graduate" ? (
                            <option value="Master of Public Policy (MPP)">
                              Master of Public Policy (MPP)
                            </option>
                          ) : null}
                        </select>

                        {errors.secondPriorityProgram && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.secondPriorityProgram}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-12">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        Third Priority Degree *
                      </h3>
                      <div className="td_mb_40">
                        <select
                          id="thirdPriorityProgram"
                          className={`td_form_field td_mb_8 td_medium td_white_bg`}
                          value={formData.thirdPriorityProgram}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              thirdPriorityProgram: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Degree</option>

                          {formData.studies === "Undergraduate" ? (
                            undergraduatePrograms.map((group) => {
                              const filteredPrograms = group.programs.filter(
                                (program) =>
                                  program !== formData.firstPriorityProgram &&
                                  program !== formData.secondPriorityProgram,
                              );

                              if (filteredPrograms.length === 0) return null;

                              return (
                                <optgroup
                                  key={group.category}
                                  label={group.category}
                                >
                                  {filteredPrograms.map((program) => (
                                    <option key={program} value={program}>
                                      {program}
                                    </option>
                                  ))}
                                </optgroup>
                              );
                            })
                          ) : formData.studies === "Graduate" ? (
                            <option value="Master of Public Policy (MPP)">
                              Master of Public Policy (MPP)
                            </option>
                          ) : null}
                        </select>

                        {errors.thirdPriorityProgram && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.thirdPriorityProgram}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Full Name */}
                  <div className="row">
                    <div className="col-lg-4">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        First Name *
                      </h3>
                      <div className="td_mb_40">
                        <input
                          id="name"
                          type="text"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.name ? "error-border" : ""
                          }`}
                          placeholder="Enter Your First Name"
                          value={formData.firstName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              firstName: e.target.value,
                            })
                          }
                        />
                        {errors.firstName && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.firstName}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        Middle Name
                      </h3>
                      <div className="td_mb_40">
                        <input
                          id="name"
                          type="text"
                          className={`td_form_field td_mb_8 td_medium td_white_bgs`}
                          placeholder="Enter Your Middle Name"
                          value={formData.middleName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              middleName: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        Last Name *
                      </h3>
                      <div className="td_mb_40">
                        <input
                          id="name"
                          type="text"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.name ? "error-border" : ""
                          }`}
                          placeholder="Enter Your Last Name"
                          value={formData.lastName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              lastName: e.target.value,
                            })
                          }
                        />
                        {errors.lastName && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.lastName}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    {/* DOB */}
                    <div className="col-lg-6">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        Date of Birth *
                      </h3>
                      <div className="td_mb_40">
                        <input
                          id="dob"
                          type="date"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.dob ? "error-border" : ""
                          }`}
                          value={formData.dob}
                          onChange={(e) =>
                            setFormData({ ...formData, dob: e.target.value })
                          }
                        />
                        {errors.dob && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.dob}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Gender */}
                    <div className="col-lg-6">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        Gender
                      </h3>
                      <div className="td_mb_40">
                        <select
                          id="gender"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.gender ? "error-border" : ""
                          }`}
                          value={formData.gender}
                          onChange={(e) =>
                            setFormData({ ...formData, gender: e.target.value })
                          }
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                        {errors.gender && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.gender}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    {/* Nationality */}
                    <div className="col-lg-6">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        Nationality
                      </h3>
                      <div className="td_mb_40">
                        <select
                          id="nationality"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.nationality ? "error-border" : ""
                          }`}
                          value={formData.nationality}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              nationality: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Nationality</option>
                          {countryOptions.map((c) => (
                            <option key={c.value} value={c.label}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                        {errors.nationality && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.nationality}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        Ethnicity
                      </h3>
                      <div className="td_mb_40">
                        <input
                          id="ethnicity"
                          type="text"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.name ? "error-border" : ""
                          }`}
                          placeholder="Enter Your Ethnicity"
                          value={formData.ethnicity}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              ethnicity: e.target.value,
                            })
                          }
                        />
                        {errors.name && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.ethnicity}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-6">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        Email *
                      </h3>
                      <div className="td_mb_40">
                        <input
                          id="email"
                          type="email"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.email ? "error-border" : ""
                          }`}
                          placeholder="Enter Your Email"
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                        />
                        {errors.email && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.email}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                        Phone *
                      </h3>
                      <div className="td_mb_40">
                        <div className="d-flex align-items-center gap-2 td_mb_8">
                          <Select
                            options={countryOptions}
                            value={countryOptions.find(
                              (c) => c.value === selectedCountry,
                            )}
                            onChange={(option) =>
                              setSelectedCountry(option!.value)
                            }
                            isSearchable={false}
                            styles={selectStyles}
                            formatOptionLabel={(option, { context }) => {
                              if (context === "menu") {
                                return (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "5px",
                                    }}
                                  >
                                    <img
                                      src={`https://flagcdn.com/16x12/${option.iso2}.png`}
                                      alt={option.label}
                                    />
                                    <span>
                                      {option.label} (+{option.dialCode})
                                    </span>
                                  </div>
                                );
                              }
                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "2px",
                                  }}
                                >
                                  <img
                                    src={`https://flagcdn.com/16x12/${option.iso2}.png`}
                                    alt={option.label}
                                  />
                                  <span>(+{option.dialCode})</span>
                                </div>
                              );
                            }}
                          />
                          <input
                            id="mobile"
                            type="tel"
                            className={`td_form_field td_medium td_white_bg ${
                              errors.mobile ? "error-border" : ""
                            }`}
                            placeholder="Enter Your Phone Number"
                            value={formData.phone}
                            maxLength={10}
                            onChange={(e) => {
                              if (/^\d*$/.test(e.target.value)) {
                                setFormData({
                                  ...formData,
                                  phone: e.target.value,
                                });
                              }
                            }}
                            style={{ flex: 1 }}
                          />
                        </div>
                        {errors.phone && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.phone}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                    Contact Information
                  </h3>
                  <div className="row">
                    <div className="col-lg-12">
                      <label className="td_medium td_heading_color td_mb_10">
                        Current Address *
                      </label>
                      <div className="td_mb_40">
                        <input
                          id="currentAddress"
                          type="text"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.currentAddress ? "error-border" : ""
                          }`}
                          placeholder="Enter Current Address"
                          value={formData.currentAddress}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormData({
                              ...formData,
                              currentAddress: val,
                              permanentAddress: sameAsCurrent
                                ? val
                                : formData.permanentAddress,
                            });
                          }}
                        />
                        {errors.currentAddress && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.currentAddress}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="d-flex justify-content-between flex-wrap">
                        <label className="td_medium td_heading_color td_mb_10">
                          Permanent Address *
                        </label>
                        <div className="td_custom_checkbox td_mb_10">
                          <input
                            type="checkbox"
                            id="sameAddr"
                            checked={sameAsCurrent}
                            onChange={(e) => {
                              setSameAsCurrent(e.target.checked);
                              if (e.target.checked) {
                                setFormData((prev) => ({
                                  ...prev,
                                  permanentAddress: prev.currentAddress,
                                }));
                              }
                            }}
                          />
                          <label htmlFor="sameAddr">
                            Same as Current Address
                          </label>
                        </div>
                      </div>
                      <div className="td_mb_40">
                        <input
                          id="permanentAddress"
                          type="text"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.permanentAddress ? "error-border" : ""
                          }`}
                          placeholder="Enter Permanent Address"
                          value={formData.permanentAddress}
                          disabled={sameAsCurrent}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              permanentAddress: e.target.value,
                            })
                          }
                        />
                        {errors.permanentAddress && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.permanentAddress}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Guardian */}
                  <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                    Guardian Information
                  </h3>
                  <div className="row">
                    <div className="col-lg-6">
                      <label className="td_medium td_heading_color td_mb_10">
                        Full Name *
                      </label>
                      <div className="td_mb_40">
                        <input
                          id="guardianName"
                          type="text"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.guardianName ? "error-border" : ""
                          }`}
                          placeholder="Enter Guardian Full Name"
                          value={formData.guardianName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              guardianName: e.target.value,
                            })
                          }
                        />
                        {errors.guardianName && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.guardianName}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <label className="td_medium td_heading_color td_mb_10">
                        Relation *
                      </label>
                      <div className="td_mb_40">
                        <input
                          id="guardianRelation"
                          type="text"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.guardianRelation ? "error-border" : ""
                          }`}
                          placeholder="Enter Relation"
                          value={formData.guardianRelation}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              guardianRelation: e.target.value,
                            })
                          }
                        />
                        {errors.guardianRelation && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.guardianRelation}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Academic Qualification */}
                  <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                    Academic Qualification
                  </h3>

                  <div className="row">
                    <div className="col-lg-6">
                      <label className="td_medium td_heading_color td_mb_10">
                        10+2 or Equivalent
                      </label>
                      <div className="td_mb_40">
                        <input
                          id="institution"
                          type="text"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.institution ? "error-border" : ""
                          }`}
                          placeholder="Institution Name"
                          value={formData.plus2institution}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              plus2institution: e.target.value,
                            })
                          }
                        />
                        {errors.institution && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.institution}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <label className="td_medium td_heading_color td_mb_10">
                        Year of Graduation
                      </label>
                      <div className="td_mb_40">
                        <input
                          id="graduationYear"
                          type="number"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.graduationYear ? "error-border" : ""
                          }`}
                          placeholder="Year"
                          value={formData.yearOfGraduation}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              yearOfGraduation: e.target.value,
                            })
                          }
                        />
                        {errors.graduationYear && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.graduationYear}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="td_mb_40">
                    <label className="td_medium td_heading_color td_mb_10">
                      Do you have any other degrees?
                    </label>
                    <div className="td_contact_box td_radius_10 td_mb_40 td_white_bg td_form_section">
                      <div className="row">
                        {[
                          {
                            key: "otherDegree",
                            label: "",
                            state: otherDegreeYesNo,
                            setState: setOtherDegreeYesNo,
                            fileKey: "otherDegreeFile" as AwardsFileKey,
                            ref: otherDegreeFileRef,
                          },
                        ].map((item) => {
                          const file = formData[item.fileKey];
                          const filePreviewUrl = file
                            ? URL.createObjectURL(file)
                            : null;
                          return (
                            <div className="col-lg-12" key={item.key}>
                              <label className="td_medium td_heading_color td_mb_10">
                                {item.label}
                              </label>
                              <div className="td_mb_40">
                                <div
                                  className={`d-flex ${
                                    !file &&
                                    "justify-content-between align-items-center"
                                  } ${file && "gap-5"}`}
                                >
                                  <div
                                    className="td_mb_8 d-flex gap-5 flex-wrap"
                                    id={item.key}
                                  >
                                    <label>
                                      <input
                                        type="radio"
                                        name={item.key}
                                        value="yes"
                                        checked={item.state === "yes"}
                                        onChange={(e) =>
                                          item.setState(e.target.value)
                                        }
                                      />{" "}
                                      Yes
                                    </label>
                                    <label className="td_ml_20">
                                      <input
                                        type="radio"
                                        name={item.key}
                                        value="no"
                                        checked={item.state === "no"}
                                        onChange={(e) =>
                                          item.setState(e.target.value)
                                        }
                                      />{" "}
                                      No
                                    </label>
                                  </div>
                                  {item.state === "yes" &&
                                    (file ? (
                                      <div
                                        className="td_card_image"
                                        style={{
                                          position: "relative",
                                          cursor: "default",
                                          marginLeft: "20px",
                                        }}
                                      >
                                        {file.type === "application/pdf" ? (
                                          <iframe
                                            src={filePreviewUrl!}
                                            title={`${item.label} PDF Preview`}
                                            style={{
                                              width: "80px",
                                              height: "80px",
                                              border: "1px solid #ccc",
                                            }}
                                          />
                                        ) : (
                                          <img
                                            src={filePreviewUrl!}
                                            alt={`${item.label} Preview`}
                                            style={{
                                              width: "80px",
                                              height: "80px",
                                              objectFit: "cover",
                                            }}
                                          />
                                        )}
                                        <div
                                          className="edit-pic-icon"
                                          onClick={() =>
                                            item.ref.current?.click()
                                          }
                                        >
                                          <span className="fa-solid fa-pen-to-square"></span>
                                        </div>
                                      </div>
                                    ) : (
                                      <label
                                        htmlFor={item.fileKey}
                                        className={`td_form_field td_mb_8 td_medium td_white_bg custom_certification_width ${
                                          errors[item.fileKey]
                                            ? "error-border"
                                            : ""
                                        }`}
                                      >
                                        Click to upload
                                      </label>
                                    ))}
                                  <input
                                    ref={item.ref}
                                    id={item.fileKey}
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.pdf"
                                    style={{ display: "none" }}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [item.fileKey]:
                                          e.target.files?.[0] || null,
                                      }))
                                    }
                                  />
                                </div>
                                {errors[item.key] && (
                                  <p className="text-danger td_fs_14 td_mb_8">
                                    {errors[item.key]}
                                  </p>
                                )}
                                {errors[item.fileKey] && (
                                  <p className="text-danger td_fs_14 td_mb_8">
                                    {errors[item.fileKey]}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Publications / Awards */}
                  <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                    Awards / Certificates
                  </h3>
                  <div className="td_contact_box td_radius_10 td_mb_40 td_white_bg td_form_section">
                    <div className="row">
                      {[
                        {
                          key: "academicAward",
                          label: "Have you received any awards / certificates?",
                          state: academicAwardYesNo,
                          setState: setAcademicAwardYesNo,
                          fileKey: "academicAwardFile" as AwardsFileKey,
                          ref: academicAwardFileRef,
                        },
                      ].map((item) => {
                        const file = formData[item.fileKey];
                        const filePreviewUrl = file
                          ? URL.createObjectURL(file)
                          : null;
                        return (
                          <div className="col-lg-12" key={item.key}>
                            <label className="td_medium td_heading_color td_mb_10">
                              {item.label}
                            </label>
                            <div className="td_mb_40">
                              <div
                                className={`d-flex ${
                                  !file &&
                                  "justify-content-between align-items-center"
                                } ${file && "gap-5"}`}
                              >
                                <div
                                  className="td_mb_8 d-flex gap-5 flex-wrap"
                                  id={item.key}
                                >
                                  <label>
                                    <input
                                      type="radio"
                                      name={item.key}
                                      value="yes"
                                      checked={item.state === "yes"}
                                      onChange={(e) =>
                                        item.setState(e.target.value)
                                      }
                                    />{" "}
                                    Yes
                                  </label>
                                  <label className="td_ml_20">
                                    <input
                                      type="radio"
                                      name={item.key}
                                      value="no"
                                      checked={item.state === "no"}
                                      onChange={(e) =>
                                        item.setState(e.target.value)
                                      }
                                    />{" "}
                                    No
                                  </label>
                                </div>
                                {item.state === "yes" &&
                                  (file ? (
                                    <div
                                      className="td_card_image"
                                      style={{
                                        position: "relative",
                                        cursor: "default",
                                      }}
                                    >
                                      {file.type === "application/pdf" ? (
                                        <iframe
                                          src={filePreviewUrl!}
                                          title={`${item.label} PDF Preview`}
                                          style={{
                                            width: "80px",
                                            height: "80px",
                                            border: "1px solid #ccc",
                                          }}
                                        />
                                      ) : (
                                        <img
                                          src={filePreviewUrl!}
                                          alt={`${item.label} Preview`}
                                          style={{
                                            width: "80px",
                                            height: "80px",
                                            objectFit: "cover",
                                          }}
                                        />
                                      )}
                                      <div
                                        className="edit-pic-icon"
                                        onClick={() =>
                                          item.ref.current?.click()
                                        }
                                      >
                                        <span className="fa-solid fa-pen-to-square"></span>
                                      </div>
                                    </div>
                                  ) : (
                                    <label
                                      htmlFor={item.fileKey}
                                      className={`td_form_field td_mb_8 td_medium td_white_bg custom_certification_width ${
                                        errors[item.fileKey]
                                          ? "error-border"
                                          : ""
                                      }`}
                                    >
                                      Click to upload
                                    </label>
                                  ))}
                                <input
                                  ref={item.ref}
                                  id={item.fileKey}
                                  type="file"
                                  accept=".png,.jpg,.jpeg,.pdf"
                                  style={{ display: "none" }}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      [item.fileKey]:
                                        e.target.files?.[0] || null,
                                    }))
                                  }
                                />
                              </div>
                              {errors[item.key] && (
                                <p className="text-danger td_fs_14 td_mb_8">
                                  {errors[item.key]}
                                </p>
                              )}
                              {errors[item.fileKey] && (
                                <p className="text-danger td_fs_14 td_mb_8">
                                  {errors[item.fileKey]}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Employment History */}
                  <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                    Internship / Volunteering Experience
                  </h3>
                  <div className="td_contact_box td_radius_10 td_mb_40 td_white_bg td_form_section">
                    <div className="row">
                      <div className="col-lg-12">
                        <label className="td_medium td_heading_color td_mb_10">
                          Do you have any internship / volunteering experience?
                        </label>
                        <div className="td_mb_40">
                          <div className="d-flex justify-content-between align-items-center gap-5">
                            <div
                              className="td_mb_8 d-flex gap-5 flex-wrap"
                              id="internYesNo"
                            >
                              <label>
                                <input
                                  type="radio"
                                  name="intern"
                                  value="yes"
                                  checked={internYesNo === "yes"}
                                  onChange={(e) =>
                                    setInternYesNo(e.target.value)
                                  }
                                />{" "}
                                Yes
                              </label>
                              <label className="td_ml_20">
                                <input
                                  type="radio"
                                  name="intern"
                                  value="no"
                                  checked={internYesNo === "no"}
                                  onChange={(e) =>
                                    setInternYesNo(e.target.value)
                                  }
                                />{" "}
                                No
                              </label>
                            </div>
                            {internYesNo === "yes" && (
                              <div className="flex-1">
                                <input
                                  id="internOrg"
                                  type="text"
                                  className={`td_form_field td_mb_12 td_medium td_white_bg ${
                                    errors.internOrg ? "error-border" : ""
                                  }`}
                                  placeholder="Organization Name"
                                  value={formData.internOrg}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      internOrg: e.target.value,
                                    }))
                                  }
                                />
                                <input
                                  id="internDuration"
                                  type="number"
                                  min="0"
                                  onWheel={(e) => e.currentTarget.blur()}
                                  className={`td_form_field td_mb_8 td_medium td_white_bg ${
                                    errors.internDuration ? "error-border" : ""
                                  }`}
                                  placeholder="Duration (months)"
                                  value={formData.internDuration}
                                  onChange={(e) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      internDuration: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                            )}
                          </div>
                          {errors.internYesNo && (
                            <p className="text-danger td_fs_14 td_mb_8">
                              {errors.internYesNo}
                            </p>
                          )}
                          {errors.internOrg && (
                            <p className="text-danger td_fs_14 td_mb_8">
                              {errors.internOrg}
                            </p>
                          )}
                          {errors.internDuration && (
                            <p className="text-danger td_fs_14 td_mb_8">
                              {errors.internDuration}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Finance Plan */}
                  <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                    How do you plan to finance your education?
                  </h3>
                  <div className="td_mb_40">
                    <div
                      className="td_mb_8 d-flex gap-5 flex-wrap"
                      id="financePlan"
                    >
                      {["self-finance", "sponsorship"].map((opt) => (
                        <label key={opt} className="td_mr_30">
                          <input
                            type="radio"
                            name="finance"
                            value={opt}
                            checked={formData.financePlan === opt}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                financePlan: e.target.value,
                              })
                            }
                          />{" "}
                          {opt === "self-finance"
                            ? "Self Finance"
                            : opt.charAt(0).toUpperCase() + opt.slice(1)}
                        </label>
                      ))}
                    </div>
                    {errors.financePlan && (
                      <p className="text-danger td_fs_14 td_mb_8">
                        {errors.financePlan}
                      </p>
                    )}
                  </div>

                  <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                    Do you want to be considered for scholarship and financial
                    aid?
                  </h3>

                  <div className="td_mb_40">
                    <div className="d-flex td_mb_10 gap-5">
                      <label>
                        <input
                          type="radio"
                          value="yes"
                          checked={formData.scholarship === "yes"}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              scholarship: e.target.value,
                            })
                          }
                        />{" "}
                        Yes
                      </label>
                      <label>
                        <input
                          type="radio"
                          value="no"
                          checked={formData.scholarship === "no"}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              scholarship: e.target.value,
                            })
                          }
                        />{" "}
                        No
                      </label>
                    </div>

                    {formData.scholarship === "yes" && (
                      <div className="td_mb_20">
                        <label>
                          Select scholarship types (Choose up to 2):
                        </label>

                        <div className="chips-grid td_mb_10">
                          {[
                            "Nepal Gaurav",
                            "Merit-based",
                            "Need-based",
                            "Reserved (UGC)",
                            "Partnership",
                            "Endowment",
                          ].map((type) => {
                            const isSelected =
                              formData.scholarshipTypes.includes(type);
                            const isMaxed =
                              formData.scholarshipTypes.length >= 2 &&
                              !isSelected;

                            return (
                              <button
                                key={type}
                                type="button"
                                disabled={isMaxed}
                                className={`scholarship-chip ${isSelected ? "is-selected" : ""} ${isMaxed ? "is-disabled" : ""}`}
                                onClick={() => {
                                  let updated = [...formData.scholarshipTypes];
                                  if (isSelected) {
                                    updated = updated.filter((t) => t !== type);
                                  } else if (updated.length < 2) {
                                    updated.push(type);
                                  }
                                  setFormData({
                                    ...formData,
                                    scholarshipTypes: updated,
                                  });
                                }}
                              >
                                {type}
                                {isSelected && (
                                  <span className="chip-icon">✓</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div
                      className="d-flex align-items-center gap-2"
                      style={{
                        textDecoration: "underline",
                        color: "#2f2b75",
                        cursor: "pointer",
                      }}
                      onClick={() => openOffcanvas("scholarship")}
                    >
                      <i className="fa-solid fa-file-text"></i>
                      Financial Aid and Scholarships
                    </div>

                    {errors.scholarship && (
                      <p className="text-danger td_fs_14 td_mb_8">
                        {errors.scholarship}
                      </p>
                    )}
                  </div>

                  {/* Referee & Supporting Document */}
                  <div className="row td_mt_40">
                    <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                      Referees (One of them should be your former teacher or
                      instructor)
                    </h3>
                    <label className="td_medium td_heading_color td_mb_10">
                      Referee One
                    </label>
                    <div className="col-lg-12">
                      <div className="td_mb_40">
                        <input
                          id="referee1Name"
                          type="text"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.referee1Name ? "error-border" : ""
                          }`}
                          placeholder="Enter Full Name"
                          value={formData.referee1Name}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormData({
                              ...formData,
                              referee1Name: val,
                            });
                          }}
                        />
                        {errors.referee1Name && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.referee1Name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="td_mb_40">
                        <input
                          id="referee1Email"
                          type="email"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.referee1Email ? "error-border" : ""
                          }`}
                          placeholder="Enter Email"
                          value={formData.referee1Email}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              referee1Email: e.target.value,
                            })
                          }
                        />
                        {errors.referee1Email && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.referee1Email}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="td_mb_40">
                        <div className="d-flex align-items-center gap-2 td_mb_8">
                          <Select
                            options={countryOptions}
                            value={countryOptions.find(
                              (c) => c.value === refree1Country,
                            )}
                            onChange={(option) =>
                              setRefree1Country(option!.value)
                            }
                            isSearchable={false}
                            styles={selectStyles}
                            formatOptionLabel={(option, { context }) => {
                              if (context === "menu") {
                                return (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "5px",
                                    }}
                                  >
                                    <img
                                      src={`https://flagcdn.com/16x12/${option.iso2}.png`}
                                      alt={option.label}
                                    />
                                    <span>
                                      {option.label} (+{option.dialCode})
                                    </span>
                                  </div>
                                );
                              }
                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "2px",
                                  }}
                                >
                                  <img
                                    src={`https://flagcdn.com/16x12/${option.iso2}.png`}
                                    alt={option.label}
                                  />
                                  <span>(+{option.dialCode})</span>
                                </div>
                              );
                            }}
                          />
                          <input
                            id="referee1Mobile"
                            type="tel"
                            className={`td_form_field td_medium td_white_bg ${
                              errors.referee1Mobile ? "error-border" : ""
                            }`}
                            placeholder="Enter Phone"
                            value={formData.referee1Mobile}
                            maxLength={10}
                            onChange={(e) => {
                              if (/^\d*$/.test(e.target.value)) {
                                setFormData({
                                  ...formData,
                                  referee1Mobile: e.target.value,
                                });
                              }
                            }}
                            style={{ flex: 1 }}
                          />
                        </div>
                        {errors.referee1Mobile && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.referee1Mobile}
                          </p>
                        )}
                      </div>
                    </div>

                    <label className="td_medium td_heading_color td_mb_10">
                      Referee Two
                    </label>
                    <div className="col-lg-12">
                      <div className="td_mb_40">
                        <input
                          id="referee2Name"
                          type="text"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.referee2Name ? "error-border" : ""
                          }`}
                          placeholder="Enter Full Name"
                          value={formData.referee2Name}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormData({
                              ...formData,
                              referee2Name: val,
                            });
                          }}
                        />
                        {errors.referee2Name && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.referee2Name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="td_mb_40">
                        <input
                          id="referee2Email"
                          type="email"
                          className={`td_form_field td_mb_8 td_medium td_white_bg ${
                            errors.referee2Email ? "error-border" : ""
                          }`}
                          placeholder="Enter Email"
                          value={formData.referee2Email}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              referee2Email: e.target.value,
                            })
                          }
                        />
                        {errors.referee2Email && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.referee2Email}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="td_mb_40">
                        <div className="d-flex align-items-center gap-2 td_mb_8">
                          <Select
                            options={countryOptions}
                            value={countryOptions.find(
                              (c) => c.value === refree2Country,
                            )}
                            onChange={(option) =>
                              setRefree2Country(option!.value)
                            }
                            isSearchable={false}
                            styles={selectStyles}
                            formatOptionLabel={(option, { context }) => {
                              if (context === "menu") {
                                return (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "5px",
                                    }}
                                  >
                                    <img
                                      src={`https://flagcdn.com/16x12/${option.iso2}.png`}
                                      alt={option.label}
                                    />
                                    <span>
                                      {option.label} (+{option.dialCode})
                                    </span>
                                  </div>
                                );
                              }
                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "2px",
                                  }}
                                >
                                  <img
                                    src={`https://flagcdn.com/16x12/${option.iso2}.png`}
                                    alt={option.label}
                                  />
                                  <span>(+{option.dialCode})</span>
                                </div>
                              );
                            }}
                          />
                          <input
                            id="referee2Mobile"
                            type="tel"
                            className={`td_form_field td_medium td_white_bg ${
                              errors.referee2Mobile ? "error-border" : ""
                            }`}
                            placeholder="Enter Phone"
                            value={formData.referee2Mobile}
                            maxLength={10}
                            onChange={(e) => {
                              if (/^\d*$/.test(e.target.value)) {
                                setFormData({
                                  ...formData,
                                  referee2Mobile: e.target.value,
                                });
                              }
                            }}
                            style={{ flex: 1 }}
                          />
                        </div>
                        {errors.referee2Mobile && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors.referee2Mobile}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Supporting Document */}
                  {/* <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                  Supporting Documents
                </h3>
                <div className="td_contact_box td_radius_10 td_mb_40 td_white_bg td_form_section">
                  <div className="row">
                    {[
                      {
                        key: "citizenship",
                        label: "Citizenship *",
                        fileKey: "citizenshipFile",
                        ref: citizenshipFileRef,
                      },
                      {
                        key: "cv",
                        label: "CV *",
                        fileKey: "cvFile",
                        ref: cvFileRef,
                      },
                      {
                        key: "certificate",
                        label: "Certificate *",
                        fileKey: "certificateFile",
                        ref: certificateFileRef,
                      },
                      {
                        key: "Profile",
                        label: "Profile Photo *",
                        fileKey: "profileFile",
                        ref: profileFileRef,
                      },
                    ].map((item) => (
                      <div className="col-lg-6" key={item.key}>
                        <label className="td_medium td_heading_color td_mb_10">
                          {item.label}
                        </label>
                        <div className="td_mb_40">
                          <div className="d-flex justify-content-between">
                            <input
                              ref={item.ref}
                              id={item.fileKey}
                              type="file"
                              accept=".png,.jpg,.jpeg,.pdf"
                              className={`td_form_field td_mb_8 td_medium td_white_bg custom_td_form_field ${
                                errors[item.fileKey] ? "error-border" : ""
                              }`}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  [item.fileKey]: e.target.files?.[0] || null,
                                }))
                              }
                            />
                          </div>
                          {errors[item.fileKey] && (
                            <p className="text-danger td_fs_14 td_mb_8">
                              {errors[item.fileKey]}
                            </p>
                          )}
                        </div>
                      </div>
                    ))} */}
                  {/* Supporting Document */}
                  <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                    Supporting Documents
                  </h3>
                  <div className="td_contact_box td_radius_10 td_mb_40 td_white_bg td_form_section">
                    <div className="row">
                      {[
                        {
                          key: "citizenship",
                          label: "Citizenship / National ID",
                          fileKey: "citizenshipFile" as DocumentFileKey,
                          ref: citizenshipFileRef,
                        },
                        {
                          key: "cv",
                          label: "CV / Resume",
                          fileKey: "cvFile" as DocumentFileKey,
                          ref: cvFileRef,
                        },
                        {
                          key: "sop",
                          label: "Statement of Purpose (SOP)",
                          fileKey: "sopFile" as DocumentFileKey,
                          ref: sopFileRef,
                        },
                        {
                          key: "seeCertificate",
                          label: "SEE Certificate",
                          fileKey: "seeCertificateFile" as DocumentFileKey,
                          ref: seeCertificateFileRef,
                        },
                        {
                          key: "plus2certificate",
                          label: "10+2 or Equivalent Certificate",
                          fileKey: "plus2certificateFile" as DocumentFileKey,
                          ref: plus2certificateFileRef,
                        },
                        {
                          key: "profile",
                          label: "Profile Photo",
                          fileKey: "profileFile" as DocumentFileKey,
                          ref: profileFileRef,
                        },
                      ].map((item) => {
                        const file = formData[item.fileKey];
                        const filePreviewUrl = file
                          ? URL.createObjectURL(file)
                          : null;
                        return (
                          <div className="col-lg-6" key={item.key}>
                            <label className="td_medium td_heading_color td_mb_10">
                              {item.label}
                            </label>
                            <div className="td_mb_40">
                              {file ? (
                                <div
                                  className="td_card_image"
                                  style={{
                                    position: "relative",
                                    cursor: "default",
                                  }}
                                >
                                  {file.type === "application/pdf" ? (
                                    <iframe
                                      src={filePreviewUrl!}
                                      title={`${item.label} PDF Preview`}
                                      style={{
                                        width: "80px",
                                        height: "80px",
                                        border: "1px solid #ccc",
                                      }}
                                    />
                                  ) : (
                                    <img
                                      src={filePreviewUrl!}
                                      alt={`${item.label} Preview`}
                                      style={{
                                        width: "80px",
                                        height: "80px",
                                        objectFit: "cover",
                                      }}
                                    />
                                  )}
                                  <div
                                    className="edit-pic-icon"
                                    onClick={() => item.ref.current?.click()}
                                  >
                                    <span className="fa-solid fa-pen-to-square"></span>
                                  </div>
                                </div>
                              ) : (
                                <label
                                  htmlFor={item.fileKey}
                                  className={`td_form_field td_mb_8 td_medium td_white_bg custom_td_form_field ${
                                    errors[item.fileKey] ? "error-border" : ""
                                  }`}
                                >
                                  Click to upload
                                </label>
                              )}
                              <input
                                ref={item.ref}
                                id={item.fileKey}
                                type="file"
                                accept=".png,.jpg,.jpeg,.pdf"
                                style={{ display: "none" }}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    [item.fileKey]: e.target.files?.[0] || null,
                                  }))
                                }
                              />
                              {errors[item.fileKey] && (
                                <p className="text-danger td_fs_14 td_mt_5">
                                  {errors[item.fileKey]}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      <div className="td_mb_40 col-lg-8 w-100" key="Payment">
                        <p className="td_medium td_heading_color td_mb_10">
                          Please submit the screenshot of the payment as a proof
                          *
                        </p>
                        <div
                          style={{
                            flexDirection: mobile || tab ? "column" : "row",
                          }}
                          className="d-flex justify-content-between td_mb_10"
                        >
                          <div className="payment_details flex-1">
                            <p>Account Number: 1870100000304001</p>
                            <p>Account Name: University of Nepal</p>
                            <p>Bank Name: Rastriya Banijya Bank</p>
                            <p>Branch: Darbarmarg</p>
                            <p>Fee: NPR 300/-</p>
                          </div>

                          <img
                            src={bankQr}
                            alt="Bank QR Code"
                            style={{
                              width: "auto",
                              height: "150px",
                              objectFit: "contain",
                            }}
                          />
                        </div>

                        <div className="col-lg-9">
                          {formData.paymentFile ? (
                            <div
                              className="td_card_image"
                              style={{
                                cursor: "default",
                                position: "relative",
                              }}
                            >
                              {formData.paymentFile.type ===
                              "application/pdf" ? (
                                <iframe
                                  src={URL.createObjectURL(
                                    formData.paymentFile,
                                  )}
                                  title="Payment PDF Preview"
                                  style={{
                                    width: "80px",
                                    height: "80px",
                                    border: "1px solid #ccc",
                                  }}
                                />
                              ) : (
                                <img
                                  src={URL.createObjectURL(
                                    formData.paymentFile,
                                  )}
                                  alt="Payment Preview"
                                  style={{
                                    width: "80px",
                                    height: "80px",
                                    objectFit: "cover",
                                  }}
                                />
                              )}

                              {/* Edit icon */}
                              <div
                                className="edit-pic-icon"
                                // style={{ top: "0px", left: "65px" }}
                                onClick={() => paymentFileRef.current?.click()}
                              >
                                <span className="fa-solid fa-pen-to-square"></span>
                              </div>
                            </div>
                          ) : (
                            <label
                              htmlFor="paymentFile"
                              className={`td_form_field td_mb_8 td_medium td_white_bg custom_td_form_field`}
                            >
                              Click to upload
                            </label>
                          )}
                          <input
                            ref={paymentFileRef}
                            id="paymentFile"
                            type="file"
                            accept=".png,.jpg,.jpeg,.pdf"
                            style={{ display: "none" }}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                paymentFile: e.target.files?.[0] || null,
                              }))
                            }
                          />
                        </div>

                        {errors["paymentFile"] && (
                          <p className="text-danger td_fs_14 td_mb_8">
                            {errors["paymentFile"]}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Declaration */}
                  <div className="td_mt_40">
                    <h3 className="td_fs_20 td_medium td_heading_color td_mb_10">
                      Declaration *
                    </h3>

                    <div className="td_mb_40">
                      <div className="td_mb_8 d-flex align-items-start gap-2">
                        <input
                          type="checkbox"
                          id="declaration"
                          style={{ marginTop: "5px", cursor: "pointer" }}
                          checked={formData.declaration}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              declaration: e.target.checked,
                            }))
                          }
                        />
                        <label>
                          I hereby declare and agree that all the information
                          provided in this application form is true and correct
                          to the best of my knowledge. I have read and
                          understood the{" "}
                          <span
                            style={{
                              textDecoration: "underline",
                              color: "#2f2b75",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              openOffcanvas("admission");
                            }}
                          >
                            admission policies
                          </span>{" "}
                          of the University of Nepal.
                        </label>
                      </div>
                      {errors.declaration && (
                        <p className="text-danger td_fs_14 td_mb_8">
                          {errors.declaration}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="td_form_card_btn_group_1">
                    <button
                      type="button"
                      className="td_btn td_style_1 td_radius_10 td_medium td_fs_18"
                      onClick={(e) =>
                        handleButtonClick(
                          () => handleSubmit(e),
                          "Are you sure you want to submit the form?",
                        )
                      }
                    >
                      <span className="td_btn_in td_white_color td_accent_bg">
                        <span>Submit</span>
                      </span>
                    </button>

                    <button
                      type="button"
                      className="td_btn td_style_1 td_radius_10 td_medium td_fs_18"
                      onClick={() =>
                        handleButtonClick(
                          saveToLocalStorage,
                          "Do you want to save the form data? Data will be saved only in this browser.",
                        )
                      }
                    >
                      <span className="td_btn_in td_white_color td_accent_bg">
                        <span>Save</span>
                      </span>
                    </button>

                    <button
                      type="button"
                      className="td_btn td_style_1 td_radius_10 td_medium td_fs_18"
                      onClick={() =>
                        handleButtonClick(
                          () => handleDownloadPDF(formData),
                          "Download PDF with filled data?",
                        )
                      }
                    >
                      <span className="td_btn_in td_white_color td_accent_bg">
                        <span>Download My Form</span>
                      </span>
                    </button>

                    <button
                      type="button"
                      className="td_btn td_style_1 td_radius_10 td_medium td_fs_18"
                      onClick={() =>
                        handleButtonClick(
                          resetForm,
                          "Are you sure you want to clear the form? This will also clear any saved data.",
                        )
                      }
                      style={{ backgroundColor: "#dc3545" }}
                    >
                      <span className="td_btn_in td_white_color">
                        <span>Clear</span>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="td_height_70" />
      </section>

      <PoliciesDrawer
        admissionPolicy={admissionPolicy}
        scholarshipsAndFinancial={scholarshipsAndFinancial}
      />

      <div
        className={`td_scrollup td_scrollup_show`}
        onClick={() => openOffcanvas("admission")}
      >
        <i className="fa fa-file-text"></i>
      </div>

      <p
        style={{
          position: "fixed",
          bottom: "74px",
          right: "71px",
          color: "#f58466",
          zIndex: "100",
        }}
      >
        Admission Policies
      </p>
    </>
  );
};

export default RefactoredApplicationForm;
